// The few words Console D's components say themselves (APS-412): a status's word and the code
// window's Copy. Each app supplies them in its language (the console from its catalogue), so
// the components hold no copy of their own.
import { createContext, type ReactNode, useContext } from 'react'
import type { StatusKey } from './d/Status'

export interface UiText {
  status: Record<StatusKey, string>
  copy: string
  copied: string
  copyFailed: string
  /** The charts' controls and their screen-reader words (APS-577). */
  chart: ChartText
}

export interface ChartText {
  /** The toggle that shows the chart's values as a table, and hides it again. */
  table: string
  hideTable: string
  /** The button that undoes a drag-to-zoom. */
  resetZoom: string
  /** A legend item's action that shows that series alone. */
  only: string
  /**
   * How to use the chart from the keyboard, read after its name: the arrows always; Enter to
   * open where a point opens a list; Shift and the arrows to zoom on a time axis.
   */
  keys: (can: { open: boolean; zoom: boolean }) => string
  /** A point with no value (a day with nothing to measure). */
  none: string
  /** What a chart is, as its focus box announces it (aria-roledescription): "chart". */
  roleName: string
  /** The table's value column for a chart of one unnamed series. */
  value: string
  /** The table's first column when the chart names none. */
  x: string
  /** What the zoomed chart shows, for screen readers: "Showing Oct 3 to Oct 9". */
  zoomed: (from: string, to: string) => string
}

const UiTextContext = createContext<UiText | null>(null)

export function UiTextProvider({ value, children }: { value: UiText; children: ReactNode }) {
  return <UiTextContext.Provider value={value}>{children}</UiTextContext.Provider>
}

/** The app's words; a component outside a UiTextProvider is a wiring bug, so it says so. */
export function useUiText(): UiText {
  const text = useContext(UiTextContext)
  if (!text)
    throw new Error('@apsio/ui: a component needs its words; wrap the app in UiTextProvider')
  return text
}

/** The charts' words (APS-577). */
export const useChartText = (): ChartText => useUiText().chart
